import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { usePensionContracts, usePensionSummary } from "../hooks/pension";
import type { PensionContractKind } from "../lib/pension";

const KIND_ORDER: PensionContractKind[] = ["GESETZLICH", "BAV", "RIESTER", "RUERUP", "PRIVAT"];

function formatEur(cents: number | null): string {
  if (cents === null) return "—";
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString("de-DE", { timeZone: "UTC" });
}

export function PensionList() {
  const { t } = useTranslation();
  const list = usePensionContracts(true);
  const summary = usePensionSummary();

  if (list.isLoading) {
    return (
      <div className="fv-card p-10 text-center" style={{ color: "var(--muted)" }}>
        …
      </div>
    );
  }
  const contracts = list.data?.contracts ?? [];
  const isEmpty = contracts.length === 0;

  const grouped = KIND_ORDER.map((k) => ({
    kind: k,
    items: contracts.filter((c) => c.kind === k),
  })).filter((g) => g.items.length > 0);

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div
            className="text-xs font-semibold uppercase tracking-wider mb-1"
            style={{ color: "var(--muted)" }}
          >
            {t("nav.group_cashflow")}
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{t("pension.list.title")}</h1>
          {summary.data && summary.data.contractCount > 0 && (
            <div className="text-sm mt-1" style={{ color: "var(--muted)" }}>
              {summary.data.contractCount}{" "}
              {t("pension.contract_plural", { defaultValue: "Vertraege" })}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Link
            to="/pension/fire"
            className="px-3 py-2 rounded-lg border text-sm font-medium"
            style={{ borderColor: "var(--border)", background: "var(--card)" }}
          >
            {t("pension.list.fire_link")}
          </Link>
          <Link
            to="/pension/new"
            className="px-3 py-2 rounded-lg text-white text-sm font-medium"
            style={{ background: "var(--accent)" }}
          >
            + {t("pension.list.new")}
          </Link>
        </div>
      </div>

      {summary.data && summary.data.contractCount > 0 && (
        <div
          className="fv-card grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x"
          style={{ borderColor: "var(--border)" }}
        >
          <StatMini
            label={t("pension.list.summary.count")}
            value={String(summary.data.contractCount)}
          />
          <StatMini
            label={t("pension.list.summary.monthly_contribution")}
            value={formatEur(summary.data.totalMonthlyContributionCents)}
          />
          <StatMini
            label={t("pension.list.summary.projected_pension")}
            value={formatEur(summary.data.totalProjectedMonthlyPensionCents)}
            tone="pos"
            emphasis
          />
        </div>
      )}

      {isEmpty ? (
        <div className="fv-card p-10 text-center">
          <p style={{ color: "var(--text)" }} className="mb-3">
            {t("pension.list.empty")}
          </p>
          <Link
            to="/pension/new"
            className="inline-block px-3 py-2 rounded-lg text-white text-sm font-medium"
            style={{ background: "var(--accent)" }}
          >
            {t("pension.list.empty_cta")}
          </Link>
        </div>
      ) : (
        <div className="fv-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr
                className="text-left text-xs"
                style={{ background: "var(--border-soft)", color: "var(--muted)" }}
              >
                <th className="px-3 py-2 font-semibold">{t("pension.list.column.name")}</th>
                <th className="px-3 py-2 font-semibold">{t("pension.list.column.provider")}</th>
                <th className="px-3 py-2 font-semibold">{t("pension.list.column.start_date")}</th>
                <th className="px-3 py-2 text-right font-semibold">
                  {t("pension.list.column.monthly_contribution")}
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  {t("pension.list.column.projected_pension")}
                </th>
              </tr>
            </thead>
            {grouped.map((g) => (
              <tbody key={g.kind}>
                <tr style={{ background: "var(--card-elev)" }}>
                  <td colSpan={5} className="px-3 py-2 font-semibold">
                    {t(`pension.kind.${g.kind}`)}
                  </td>
                </tr>
                {g.items.map((c) => (
                  <tr
                    key={c.id}
                    className="border-b transition-colors hover:bg-[var(--row-hover)]"
                    style={{ borderColor: "var(--border-soft)" }}
                  >
                    <td className="px-3 py-2.5">
                      <Link to={`/pension/${c.id}`} style={{ color: "var(--accent)" }}>
                        {c.name}
                      </Link>
                    </td>
                    <td className="px-3 py-2.5" style={{ color: "var(--muted)" }}>
                      {c.provider ?? "—"}
                    </td>
                    <td className="px-3 py-2.5">{formatDate(c.startDate)}</td>
                    <td className="px-3 py-2.5 text-right num">
                      {formatEur(c.monthlyContributionCents)}
                    </td>
                    <td className="px-3 py-2.5 text-right num font-semibold" style={{ color: "var(--pos)" }}>
                      {formatEur(c.projectedMonthlyPensionCents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      )}
    </section>
  );
}

function StatMini({
  label,
  value,
  tone,
  emphasis,
}: {
  label: string;
  value: string;
  tone?: "pos" | "neg";
  emphasis?: boolean;
}) {
  const color = tone === "pos" ? "var(--pos)" : tone === "neg" ? "var(--neg)" : "var(--text)";
  return (
    <div className="p-4">
      <div className="fv-kpi-label mb-1">{label}</div>
      <div
        className={`${emphasis ? "text-2xl" : "text-xl"} font-bold tracking-tight num`}
        style={{ color }}
      >
        {value}
      </div>
    </div>
  );
}
